import { useState, useEffect, useRef } from "react";
import { Button } from "src/@/components/ui/button";
import { Input } from "src/@/components/ui/input";
import { Label } from "src/@/components/ui/label";
import { Dialog, DialogTrigger, DialogContent, DialogTitle } from "src/@/components/ui/dialog";
import { ScrollArea } from "src/@/components/ui/scroll-area";
import getActionPesquisarChat from "src/actions/chats/getActionPesquisarChat";
import { formataNumeroCelular } from "src/utils/functions";

const MenuChannelChat = ({ open, onClose, cliente, user, onSelectChat }) => {
  const [listaContatos, setListaContatos] = useState([]);
  const [pesquisa, setPesquisa] = useState("");
  const [contatoWhatsappId, setContatoWhatsappId] = useState(null);
  const [modalAlerta, setModalAlerta] = useState(false);
  const [modalAlertaMensagem, setModalAlertaMensagem] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const timeoutRef = useRef(null);

  // Limpar pesquisa quando o modal fechar
  useEffect(() => {
    if (!open) {
      setPesquisa("");
      setListaContatos([]);
    }
  }, [open]);

  // Limpar timeout quando componente desmontar
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const pesquisarChat = async (value) => {
    try {
      setIsLoading(true);
      console.log('🔍 Pesquisando chat:', value);
      
      if (!cliente?._id) {
        console.error('❌ Cliente ID não encontrado');
        setListaContatos([]);
        return;
      }

      const data = {
        clienteId: cliente._id,
        search: value,
      };
      
      const result = await getActionPesquisarChat(data);
      console.log('📥 Resposta da API:', result?.data);
      
      // Verificar diferentes estruturas possíveis da resposta
      let chats = [];
      if (result?.data?.chats) {
        chats = result.data.chats;
      } else if (result?.data?.data?.chats) {
        chats = result.data.data.chats;
      } else if (result?.data && Array.isArray(result.data)) {
        chats = result.data;
      }
      
      if (Array.isArray(chats) && chats.length > 0) {
        setListaContatos(chats);
        console.log('✅ Encontrados', chats.length, 'chats');
      } else {
        setListaContatos([]);
        console.log('ℹ️ Nenhum chat encontrado');
      }
    } catch (error) {
      console.error('❌ Erro na pesquisa:', error.message);
      setListaContatos([]);
    } finally {
      setIsLoading(false);
    }
  };

  const validarInclusaoChatLead = (value) => {
    const { _id, direciona_usuarios, statusRoom } = value;
    const statusPermitidos = ["Finalizado", "Avaliando", "Geral"];

    if (statusPermitidos.includes(statusRoom)) {
      onSelectChat(value);
      setContatoWhatsappId(value);
    } else if (direciona_usuarios.length > 0 && direciona_usuarios.includes(user?.id)) {
      onSelectChat(value);
      setContatoWhatsappId(value);
    } else {
      setModalAlerta(true);
      setModalAlertaMensagem(
        "Não é possível incluir esse chat, pois ele está em andamento com outro usuário. Por favor, escolha outro chat."
      );
      setContatoWhatsappId(null);
    }
  };

  const handlePesquisaChange = (e) => {
    const value = e.target.value;
    setPesquisa(value);
    
    // Limpar timeout anterior
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Limpar resultados se muito curto
    if (value.length < 3) {
      setListaContatos([]);
      return;
    }
    
    // Debounce de 500ms
    timeoutRef.current = setTimeout(() => {
      console.log('Pesquisando após debounce para:', value);
      pesquisarChat(value);
    }, 500);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[400px]">
        <DialogTitle>Escolher chat</DialogTitle>

        {modalAlerta && (
          <Dialog open={modalAlerta} onOpenChange={() => setModalAlerta(false)}>
            <DialogContent>
              <DialogTitle>Alerta</DialogTitle>
              <p className="text-sm text-muted-foreground">{modalAlertaMensagem}</p>
              <Button
                className="mt-4"
                onClick={() => {
                  setModalAlerta(false);
                  setModalAlertaMensagem("");
                }}
              >
                Fechar
              </Button>
            </DialogContent>
          </Dialog>
        )}

        <div className="space-y-2">
          <div>
            <Label htmlFor="search">Pesquisar WhatsApp</Label>
            <div className="flex gap-2">
              <Input
                id="search"
                placeholder="Digite pelo menos 3 caracteres para pesquisar"
                value={pesquisa}
                onChange={handlePesquisaChange}
                disabled={isLoading}
                className="flex-1"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (cliente?._id) {
                    console.log('Testando pesquisa com cliente:', cliente._id);
                    pesquisarChat('teste');
                  } else {
                    console.error('Cliente não disponível para teste');
                  }
                }}
                disabled={!cliente?._id}
              >
                Teste
              </Button>
            </div>
          </div>
          
          <ScrollArea className="h-40 border rounded-md">
            {isLoading ? (
              <p className="text-sm text-muted-foreground p-2 text-center">
                Pesquisando...
              </p>
            ) : listaContatos.length > 0 ? (
              listaContatos.map((contato) => (
                <Button
                  key={contato._id}
                  variant="ghost"
                  className="w-full text-left justify-start p-2 h-auto"
                  onClick={() => validarInclusaoChatLead(contato)}
                >
                  <div className="flex flex-col items-start">
                    <span className="font-medium">
                      {formataNumeroCelular(contato.numeroCliente)}
                    </span>
                    {contato.statusRoom && (
                      <span className="text-xs text-muted-foreground">
                        Status: {contato.statusRoom}
                      </span>
                    )}
                  </div>
                </Button>
              ))
            ) : pesquisa.length >= 3 ? (
              <p className="text-sm text-muted-foreground p-2 text-center">
                Nenhum resultado encontrado para "{pesquisa}"
              </p>
            ) : (
              <p className="text-sm text-muted-foreground p-2 text-center">
                Digite pelo menos 3 caracteres para começar a pesquisar
              </p>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MenuChannelChat;
